import { ITEM_TYPES } from '@/trendData/trendData.constants';
import type { BaseItem, LaneReservationOptions } from '@/trendData/trendData.types';
import type { WithProperty } from '@/utilities.types';

export interface ReservationResult {
  lane: number;
  axisAlign?: string;
}

/**
 * Find the lane (and axisAlign) used by items with the given reservation tag.
 * If a matching item exists, returns its lane and axisAlign so the new item joins the same group on the same axis.
 * If no items match the tag and above/below is provided, find the anchor reservation's lane and insert adjacent
 * to it by bumping other items out of the way.
 * Returns undefined only if neither the tag nor the anchor is found.
 */
export function getLaneForReservation(
  reservation: string,
  options?: LaneReservationOptions,
  setCustomizationProps?: (items: WithProperty<{ id: string }>[]) => void,
  getAlignableItems?: () => BaseItem[],
): ReservationResult | undefined {
  if (!getAlignableItems) {
    return undefined;
  }
  const items = getAlignableItems() as BaseItem[];
  const match = items.find((item) => item.laneReservation === reservation);
  if (match) {
    return { lane: match.lane, axisAlign: match.axisAlign };
  }

  const anchorTag = options?.above ?? options?.below;
  if (anchorTag) {
    const anchor = items.find((item) => item.laneReservation === anchorTag);
    if (anchor) {
      const insertLane = options?.above ? anchor.lane : anchor.lane + 1;
      const toBump = items
        .filter((item) => item.lane >= insertLane)
        .map((item) => ({
          id: item.id,
          lane: item.lane + 1,
          ...(item.overlay !== undefined ? { overlay: item.overlay } : {}),
        }));
      if (toBump.length > 0 && setCustomizationProps) {
        setCustomizationProps(toBump);
      }
      return { lane: insertLane, axisAlign: anchor.axisAlign };
    }
  }

  return undefined;
}

/**
 * Reindex orderInLane values for all items sharing a laneReservation tag.
 * Items are sorted by orderInLane and assigned contiguous 1-based values (1, 2, 3, ...)
 */
export function reindexOrderInLaneForReservation(
  reservation: string,
  setCustomizationProps: (items: WithProperty<{ id: string }>[]) => void,
  getAlignableItems: () => BaseItem[],
): void {
  const items = getAlignableItems() as BaseItem[];
  const group = items
    .filter(
      (item) =>
        item.itemType === ITEM_TYPES.CONDITION &&
        item.laneReservation === reservation &&
        (item.orderInLanePriority !== undefined || item.orderInLane !== undefined),
    )
    .sort((a, b) => (a.orderInLanePriority ?? a.orderInLane ?? 0) - (b.orderInLanePriority ?? b.orderInLane ?? 0));

  if (group.length === 0) return;

  const updates = group
    .map((item, index: number) => ({
      id: item.id,
      orderInLane: index + 1,
      ...(item.orderInLanePriority !== undefined ? { orderInLanePriority: item.orderInLanePriority } : {}),
    }))
    .filter(({ id, orderInLane }: { id: string; orderInLane: number }) => {
      const current = group.find((i) => i.id === id);
      return current?.orderInLane !== orderInLane;
    });

  if (updates.length > 0) {
    setCustomizationProps(updates);
  }
}
